<template>
    <footer class="footer">

        <div class="footer__container">

            <div class="footer__main">

                <div
                    v-if="!hideLogoBelt"
                    class="footer__brand"
                >

                    <img
                        class="footer__logo"
                        :src="'footer_brand' | staticImg"
                        :alt="'footer_brand' | staticTitle"
                    >

                    <span class="footer__eyebrow">
                        {{ 'footer_brand' | staticContentAlt }}
                    </span>

                    <h2 class="footer__brand-title">
                        {{ 'footer_brand' | staticTitle }}
                    </h2>

                    <p class="footer__brand-text">
                        {{ 'footer_brand' | staticContent }}
                    </p>

                </div>

                <div class="footer__navigation">

                    <div class="footer__column">

                        <span class="footer__column-index">
                            01
                        </span>

                        <h3>
                            {{ 'footer_services' | staticTitle }}
                        </h3>

                        <div
                            class="footer__links"
                            v-html="$options.filters.staticContent('footer_services')"
                        ></div>

                    </div>

                    <div class="footer__column">

                        <span class="footer__column-index">
                            02
                        </span>

                        <h3>
                            {{ 'footer_company' | staticTitle }}
                        </h3>

                        <div
                            class="footer__links"
                            v-html="$options.filters.staticContent('footer_company')"
                        ></div>

                    </div>

                    <div class="footer__column">

                        <span class="footer__column-index">
                            03
                        </span>

                        <h3>
                            {{ 'footer_connect' | staticTitle }}
                        </h3>

                        <div
                            class="footer__links"
                            v-html="$options.filters.staticContent('footer_connect')"
                        ></div>

                    </div>

                </div>

            </div>

            <div class="footer__bottom">

                <span class="footer__copyright">
                    © {{ cYear }} DANTRIX PRO
                </span>

                <nav class="footer__legal">

                    <router-link to="/privacy-policy">
                        {{ 'footer_legal' | staticTitle }}
                    </router-link>

                    <router-link to="/terms-and-conditions">
                        {{ 'footer_legal' | staticContent }}
                    </router-link>

                    <router-link to="/cookies">
                        {{ 'footer_legal' | staticContentAlt }}
                    </router-link>

                </nav>

            </div>

        </div>

    </footer>
</template>

<script>
export default {
    name: 'navFooter',

    props: {
        hideLogoBelt: {
            type: Boolean,
            default: false
        },

        cYear: {
            type: String,
            default: () => (
                new Date().getFullYear()
            ).toString()
        }
    }
};
</script>

<style lang="scss" scoped>
.footer {
    position: relative;

    padding: 4.375em 1.25em 1.75em;

    background:
        radial-gradient(
            circle at 10% 25%,
            rgba(233, 81, 29, .055),
            transparent 28%
        ),
        var(--color-2);

    color: var(--text-1);

    overflow: hidden;
}

.footer::before {
    content: "";

    position: absolute;

    top: -11.25em;
    right: -11.25em;

    width: 22.5em;
    height: 22.5em;

    border:
        0.0625em solid
        rgba(255, 255, 255, .035);

    border-radius: 50%;

    pointer-events: none;
}

.footer__container {
    position: relative;

    width: 100%;
    max-width: var(--container);

    margin: 0 auto;

    z-index: 2;
}

.footer__main {
    display: grid;
    grid-template-columns: 1fr;

    gap: 3.4375em;

    padding-bottom: 3.4375em;
}

.footer__brand {
    max-width: 26.875em;
}

.footer__logo {
    display: block;

    width: 11.875em;
    max-height: 5.625em;

    margin-bottom: 2em;

    object-fit: contain;
    object-position: left center;
}

.footer__eyebrow {
    display: flex;
    align-items: center;

    gap: 0.75em;

    margin-bottom: 1em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.625em;
    font-weight: 600;
    -letter-spacing: 0.1875em;

    text-transform: uppercase;

    &::before {
        content: "";

        width: 1.875em;
        height: 0.0625em;

        background: var(--color-3);
    }
}

.footer__brand-title {
    max-width: 26.25em;

    margin: 0 0 1em;

    color: var(--text-1);

    font-size: clamp(1.75em, 5vw, 2.625em);
    font-weight: 700;
    line-height: 1;
    -letter-spacing: -0.09375em;

    text-transform: uppercase;
}

.footer__brand-text {
    max-width: 24.375em;

    margin: 0;

    color: var(--text-2);

    font-size: 0.8125em;
    line-height: 1.7;
}

.footer__navigation {
    display: grid;
    grid-template-columns: 1fr;

    gap: 2.375em;
}

.footer__column {
    position: relative;

    padding-top: 1.125em;

    border-top: 0.0625em solid var(--border-1);

    h3 {
        margin: 0 0 1.25em;

        color: var(--text-1);

        font-family: var(--font-tech);
        font-size: 0.6875em;
        font-weight: 700;
        -letter-spacing: 0.125em;

        text-transform: uppercase;
    }
}

.footer__column-index {
    display: block;

    margin-bottom: 0.625em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.5625em;
    -letter-spacing: 0.09375em;
}

.footer__links {
    ::v-deep ul {
        margin: 0;
        padding: 0;

        list-style: none;
    }

    ::v-deep li {
        margin-bottom: 0.6875em;
    }

    ::v-deep a {
        position: relative;

        display: inline-flex;
        align-items: center;

        color: var(--text-2);

        font-size: 0.8125em;
        font-weight: 500;

        text-decoration: none;

        transition:
            color .3s ease,
            transform .3s ease;

        &::after {
            content: "↗";

            margin-left: 0.5em;

            color: var(--text-3);

            font-size: 0.625em;

            opacity: 0;

            transform:
                translate(-0.1875em, 0.1875em);

            transition:
                opacity .3s ease,
                transform .3s ease;
        }

        &:hover {
            color: var(--text-1);

            transform: translateX(0.1875em);

            &::after {
                opacity: 1;

                transform:
                    translate(0, 0);
            }
        }
    }
}

.footer__bottom {
    display: flex;
    flex-direction: column;

    gap: 1.125em;

    padding-top: 1.5em;

    border-top: 0.0625em solid var(--border-1);
}

.footer__copyright {
    color: var(--text-2);

    font-family: var(--font-tech);
    font-size: 0.5625em;
    font-weight: 600;
    -letter-spacing: 0.0875em;

    text-transform: uppercase;
}

.footer__legal {
    display: flex;
    flex-direction: column;

    gap: 0.625em;

    a {
        color: var(--text-2);

        font-family: var(--font-tech);
        font-size: 0.5625em;
        font-weight: 500;
        -letter-spacing: 0.0625em;

        text-decoration: none;

        text-transform: uppercase;

        transition: color .3s ease;

        &:hover {
            color: var(--text-3);
        }
    }
}

@media (min-width: 43.75em) {
    .footer {
        padding:
            5em 2.5em
            1.75em;
    }

    .footer__navigation {
        grid-template-columns:
            repeat(3, 1fr);

        gap: 2.1875em;
    }

    .footer__bottom {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        gap: 1.875em;
    }

    .footer__legal {
        flex-direction: row;
        align-items: center;

        gap: 1.25em;
    }
}

@media (min-width: 62.5em) {
    .footer {
        padding:
            5.625em 3.125em
            1.875em;
    }

    .footer__main {
        grid-template-columns:
            minmax(17.5em, .8fr)
            minmax(0, 1.5fr);

        gap: 5.625em;

        align-items: start;

        padding-bottom: 4.0625em;
    }

    .footer__navigation {
        gap: 3.125em;
    }
}

@media (min-width: 87.5em) {
    .footer {
        padding:
            6.25em 4.375em
            2em;
    }

    .footer__main {
        gap: 7.5em;
    }
}
</style>